import { checkPresent, die, log } from './core.ts';
import {
  isObject,
  isPresent,
  isString,
  pare,
  recordFromEntries,
  recordValues,
  type Json,
  type TypeGuard,
} from './identity.ts';
import { capitalize } from './strings.ts';

export const Header = {
  contentType: 'Content-Type',
  referer: 'Referer', // Easy to typo!
  authorization: 'Authorization',
  authBearer: 'Bearer',
} as const;

function mimeToLabel(mt: string) {
  const segments = mt.split('/');
  const lastSegment = checkPresent(segments.at(-1));
  const type = lastSegment.length > 4 ? capitalize(lastSegment) : lastSegment.toLocaleUpperCase();
  if (segments.length === 2) {
    const category = segments[0];
    if (category !== 'application') {
      return `${type} ${capitalize(category)} File`;
    }
  }
  return `${type} File`;
}

export class Mime<T extends string = string> {
  readonly label: string;
  readonly extensions: readonly string[];

  get extension() {
    return this.extensions.at(0);
  }

  constructor(
    readonly type: T,
    label?: string,
    // Preferred extension first.
    ...extensions: readonly `.${string}`[]
  ) {
    if (label && label.startsWith('.')) {
      extensions = [label as `.${string}`, ...extensions];
      label = undefined;
    }
    this.label = label ?? mimeToLabel(type);
    this.extensions = extensions;
  }
}

export class MimeSet<T extends string = string> {
  private readonly map: Partial<Record<T, Mime<T>>>;

  constructor(readonly mimes: Mime<T>[]) {
    this.map = recordFromEntries(mimes.map((mt) => [mt.type, mt] as const));
  }

  includes(mimeType?: string): mimeType is T {
    return isPresent(mimeType) && mimeType in this.map;
  }

  get(mimeType?: string): Mime<string> | undefined {
    return isPresent(mimeType) ? this.map[mimeType as T] : undefined;
  }
}

// TODO: Rename to MimeType.
export const ContentType = {
  text: {
    html: new Mime('text/html', '.html'),
    json: new Mime('application/json', '.json'),
    xml: new Mime('application/xml', '.xml'),
    xmlAtom: new Mime('application/atom+xml', '.atom'),
  },
  image: {
    heif: new Mime('image/heif', '.heif', '.heic'),
    jpg: new Mime('image/jpeg', '.jpg', '.jpeg'),
    png: new Mime('image/png', '.png'),
    svg: new Mime('image/svg+xml', '.svg'),
    tiff: new Mime('image/tiff', '.tiff'),
  },
  video: {
    mp4: new Mime('video/mp4', '.mp4'),
    quicktime: new Mime('video/quicktime', '.mov'),
    webm: new Mime('video/webm', '.webm'),
  },
  app: {
    binary: new Mime('application/octet-stream'),
    pdf: new Mime('application/pdf', '.pdf'),
    zip: new Mime('application/zip', '.zip'),
  },
  adobe: {
    illustrator: new Mime('application/illustrator', '.ai'),
  },
  // https://developers.google.com/drive/api/guides/mime-types
  google: {
    document: new Mime('application/vnd.google-apps.document'),
    file: new Mime('application/vnd.google-apps.file'),
    folder: new Mime('application/vnd.google-apps.folder'),
    form: new Mime('application/vnd.google-apps.form'),
    sheet: new Mime('application/vnd.google-apps.spreadsheet'),
    shortcut: new Mime('application/vnd.google-apps.shortcut'),
    slides: new Mime('application/vnd.google-apps.presentation'),
  },
  microsoft: {
    excel: new Mime('application/vnd.ms-excel', '.xls'),
    excelX: new Mime('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.xlsx'),
    powerpoint: new Mime('application/vnd.ms-powerpoint', '.ppt'),
    powerpointX: new Mime(
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      '.pptx',
    ),
    word: new Mime('application/msword', '.doc'),
    wordX: new Mime(
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.docx',
    ),
  },
} as const;

export const ContentTypes = new MimeSet(
  recordValues(ContentType).flatMap(<T extends string>(cat: Record<string, Mime<T>>) =>
    recordValues(cat),
  ),
);

export type KnownContentTypes = typeof ContentTypes extends MimeSet<infer T> ? T : never;

export function mimeTypeToLabel(mt: string) {
  const mime = ContentTypes.get(mt);
  if (isPresent(mime)) {
    return mime.label;
  }
  return mimeToLabel(mt);
}

export function hasContentType(response: Response, contentType: string): boolean {
  const header = response.headers.get(Header.contentType);
  return header?.includes(contentType) === true;
}

export async function getHtml(url: string) {
  const response = await fetch(url);
  if (response.status !== 200 || !hasContentType(response, ContentType.text.html.type)) {
    console.warn('Unexpected response', {
      url,
      status: `${response.status} ${response.statusText}`,
      contentType: response.headers.get(Header.contentType),
    });
    return undefined;
  }
  const parser = new DOMParser();
  return parser.parseFromString(await response.text(), ContentType.text.html.type);
}

export interface CheckResponseOptions {
  die?: boolean;
  debug?: boolean;
  failMessage?: string | (() => string);
}
// export function checkResponse(res: Response, header: string, options?: CheckResponseOptions): Promise<boolean>;
// export function checkResponse(res: Response, header: string, options?: CheckResponseOptions): asserts res is { ok: true };
export async function checkResponse(res: Response, header: string, options?: CheckResponseOptions) {
  const debug = options?.debug ?? false;
  if (res.ok) {
    if (debug) {
      log(header, { status: res.status, statusText: res.statusText });
    }
    return true;
  }
  let message: string | undefined;
  let details: Json | undefined;
  try {
    if (isString(options?.failMessage)) {
      message = options.failMessage;
    } else {
      message = options?.failMessage?.();
    }
    if (debug) {
      if (hasContentType(res, ContentType.text.json.type)) {
        details = (await res.json()) as Json;
      } else {
        details = { 'Content-Type': res.headers.get(Header.contentType) ?? 'unknown' };
      }
    }
  } finally {
    log(header, pare({ status: res.status, statusText: res.statusText, message, details }), {
      level: 'warn',
      verbosity: isPresent(details) ? 'max' : 'terse',
    });
    if (options?.die === true) {
      die('checkResponse', message ?? res.statusText);
    }
    return false;
  }
}

export function jsonValue<T extends Json>(
  data: Json | undefined,
  keyPath: string,
  guard: TypeGuard<T, Json>,
): T | undefined {
  if (data === undefined) {
    return undefined;
  }
  const parts = keyPath.split('.');
  for (const part of parts) {
    if (isObject(data) && part in data) {
      data = data[part];
    } else {
      return undefined;
    }
  }
  return guard(data) ? data : undefined;
}
